import React from 'react';
import { useState } from "react";
import { useSelector } from 'react-redux';
import Page from '../../components/Page';
import {
    Box,
    Typography,
    Button,
    Modal,
    Chip,
    FormControl,
    FormControlLabel,
    Switch,
    Tabs,
    Tab,
    TextField,
    Autocomplete
} from '@mui/material';
import {
    Circle,
    CheckCircle,
    Eye,
    EyeOff
} from 'react-feather';
import CompanyStatusChange from './CompanyStatusChange';
import { CompanyPrivilegesView } from './CompanyPrivilegesView';
import { sortByString } from '../../utils/sortByString';
import RulerCreator from '../rulers/RulerCreator';
import CompanyRate from './CompanyRate';
import CompanyInfoEditor from './CompanyInfoEditor';
import CompanyLicensingCreation from './CompanyLicenseCreator';
import PlanInboxTable from '../plans/PlanInboxTable';
import PlanTable from '../plans/PlanTable';
import CompanyUserView from './CompanyUserView';
import CompanyLicenseDataGrid from './CompanyLicenseView';
import CompanyRulerDataGrid from './CompanyRulerView';
import { SurveyPage } from '../surveys/SurveyPage';
import { AdminCompanyProvider } from '../../context/AdminCompanyContext';
import { useCompaniesList, useCompany, useUpdateCompanyPrivileges, useDelistRelistCompany } from '../../hooks/useCompanies';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import "./CompanyViews.css";
import { EMPTY_GUID } from '../../query/queryConfig';
import TabPanel from '../../components/TabPanel';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const CompanyViewer = () => {
    /// Company data grid for super admins shows all registered companies
    const updateCompanyPrivileges = useUpdateCompanyPrivileges();
    const delistRelistCompany = useDelistRelistCompany();
    const userId = useSelector((state) => state.UserReducer.isAuthenticated?.id);
    const [selectedComp, setSelectedComp] = React.useState(EMPTY_GUID);
    const [rowItem, setRowItem] = useState();
    const [open1, setOpen1] = React.useState(false);
    const handleOpen1 = () => setOpen1(true);
    const handleClose1 = () => setOpen1(false);

    const startDateInitialState = dayjs().startOf('year');
    const endDateInitialState = dayjs().endOf('year');
    const [startDate, setStartDate] = React.useState(startDateInitialState);
    const [endDate, setEndDate] = React.useState(endDateInitialState);
    const [exportStartDate, setExportStartDate] = React.useState(dayjs(startDateInitialState).format());
    const [exportEndDate, setExportEndDate] = React.useState(dayjs(endDateInitialState).format());
    const [listFilterDates, setListFilterDates] = React.useState(null);

    const { data: allCompanies = [] } = useCompaniesList({
        startDate: listFilterDates?.startDate,
        endDate: listFilterDates?.endDate,
    });
    const { data: privilegeCompanies = [] } = useCompaniesList();
    const { data: companyData } = useCompany({
        companyId: selectedComp,
        enabled: Boolean(selectedComp && selectedComp !== EMPTY_GUID),
    });

    const processExternal = companyData?.processExternal === true;
    const creditEnabled = companyData?.creditEnabled === true;

    let allCompaniesArray = sortByString(allCompanies ?? [], 'name');

    const defaultProps = {
        options: allCompaniesArray,
        getOptionLabel: (option) => option.name,
    };

    function editStatus(company) { // handles the double click to bring up modal to edit crop amount
        setRowItem({
            id: company.id,
            name: company.name,
            street: company.street,
            city: company.city,
            state: company.state,
            zip: company.zip,
            country: company.country,
            processExternal: company.processExternal,
            email: company.email,
            accountingEmail: company?.accountingEmail,
            phoneNumber: company.phoneNumber,
            companyStatus: company.companyStatus,
            companyRate: company.companyRate
        })
        handleOpen1();
    };

    /// change company processing status
    async function changeProcessExternalStatus(event){
        const nextProcessStatus = event.target.checked;

        await updateCompanyPrivileges.mutateAsync({
            companyId: companyData.id,
            processExternal: nextProcessStatus,
            userId,
        });
    }

    async function changeCreditStatus(event){
        const nextCreditStatus = event.target.checked;

        await updateCompanyPrivileges.mutateAsync({
            companyId: companyData.id,
            creditEnabled: nextCreditStatus,
            userId,
        });
    }

    function handlePrivilegeToggle({ company, flag, enabled }) {
        return updateCompanyPrivileges.mutateAsync({
            companyId: company.id,
            userId,
            [flag]: enabled,
        });
    }

    function filterCompany(){
        setListFilterDates({ startDate: exportStartDate, endDate: exportEndDate });
    }

    function resetCompany(){
        setListFilterDates(null);
    }

    const [value, setValue] = React.useState(0);
    const handleTabChange = (event, newValue) => {
        setValue(newValue);
    };

    function delistCompany(){
        delistRelistCompany.mutate({ companyId: selectedComp, userId });
    }

    let managePlans = true;

    const showPlans = processExternal === true;
    const companyModules = [
        { key: 'privileges', label: 'Privileges', render: () => (
            <CompanyPrivilegesView
                companies={privilegeCompanies}
                onTogglePrivilege={handlePrivilegeToggle}
            />
        )},
        { key: 'company-info', label: 'Company Info', render: () => (
            <div>
                <h4>{companyData?.name}</h4>
                <div className="companyInfoSection" style={{display:'flex'}}>
                    <CompanyInfoEditor/>
                    <div className="companyInfoSubDiv" style={{width:'50%', margin:5}}>
                        <div style={{border:'solid darkGray 2px', width:"95%", padding:5, borderRadius:"5px"}}>
                            <Typography>
                                <b>Company Credit Status:</b>
                                { companyData?.companyStatus === 0 &&
                                    <Button className="statusButton" color="primary" onClick={() => editStatus(companyData)}>Pending</Button>
                                }
                                { companyData?.companyStatus === 1 &&
                                    <Button className="statusButton" color="success" onClick={() => editStatus(companyData)}>Approved with Invoice</Button>
                                }
                                { companyData?.companyStatus === 2 &&
                                    <Button className="statusButton" color="warning" onClick={() => editStatus(companyData)}>Approved with Credit Card</Button>
                                }
                                { companyData?.companyStatus === 3 &&
                                    <Button className="statusButton" color="error" onClick={() => editStatus(companyData)}>Denied</Button>
                                }
                            </Typography>
                        </div>
                        <br/>
                        <div style={{border:'solid darkGray 2px', width:"95%", padding:5, borderRadius:"5px"}}>
                            <CompanyRate/>
                        </div>
                        <br/>
                        <div style={{border:'solid darkGray 2px', width:"95%", padding:5, borderRadius:"5px"}}>
                            <span>If company is capable of processing plans, for self or for customers?</span>
                            <br/>
                            <br/>
                            { processExternal === true ?
                                <Chip icon={<Eye/>} variant="contained" color="primary" label="For Customers"/>
                                :
                                <Chip icon={<EyeOff/>} variant="contained" color="secondary" label="For Self"/>
                            }
                            <Typography sx={{ mt: 1 }}>
                                To make this company visible to prospective customers, make sure the box below is checked.
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={processExternal === true}
                                            disabled={!companyData?.id}
                                            onChange={changeProcessExternalStatus}
                                            inputProps={{ 'aria-label': 'Processing External Plans' }}
                                        />
                                    }
                                    label="Processing External Plans"
                                />
                            </Box>
                        </div>
                        <br/>
                        <div style={{border:'solid darkGray 2px', width:"95%", padding:5, borderRadius:"5px"}}>
                            <Typography>
                                Credit lets Upload Plan users apply plan credits on the Botmark Estimate after upload.
                            </Typography>
                            <br/>
                            { creditEnabled === true ?
                                <Chip icon={<CheckCircle/>} variant="contained" color="success" label="Credit On"/>
                                :
                                <Chip icon={<Circle/>} variant="contained" color="secondary" label="Credit Off"/>
                            }
                            <Box sx={{ mt: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={creditEnabled === true}
                                            disabled={!companyData?.id}
                                            onChange={changeCreditStatus}
                                            inputProps={{ 'aria-label': 'Credit' }}
                                        />
                                    }
                                    label="Credit"
                                />
                            </Box>
                        </div>
                        <br/>
                        <div style={{border:'solid darkGray 2px', width:"95%", padding:5, borderRadius:"5px"}}>
                            <span>Is this company signed up for auto approval of estimates for credit payments?</span>
                            <br/>
                            <br/>
                            { companyData?.customerRequestEstimate === true ?
                                <Chip icon={<CheckCircle/>} variant="contained" color="success" label="Requests Estimate"/>
                                :
                                <Chip icon={<Circle/>} variant="contained" color="primary" label="No Estimate Requested"/>
                            }
                        </div>
                        <br/>
                        <div style={{border:'solid darkGray 2px', width:"95%", padding:5, borderRadius:"5px"}}>
                            <Typography>To "Delete" a Company select this option. This will not completely delete the company or users but will instead disable the company and all users under it.</Typography>
                            <Button variant='contained' color="error" style={{width:"50%", alignSelf:'center', margin:'2px'}} onClick={()=>{delistCompany()}}>Delist Company</Button>
                        </div>
                    </div>
                </div>
                <Modal
                    open={open1}
                    onClose={handleClose1}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <CompanyStatusChange
                        item={rowItem}
                        style={style}
                        handleClose1={handleClose1}
                    />
                </Modal>
            </div>
        )},
        { key: 'company-users', label: 'Company Users', render: () => <CompanyUserView/> },
        { key: 'surveys', label: 'Surveys', render: () => <SurveyPage companyIdOverride={selectedComp} companyNameOverride={companyData?.name} embedded showReceivedOverride={processExternal === true} /> },
        ...(showPlans
            ? [
                { key: 'plans', label: 'Plans', render: () => <PlanTable managePlans={managePlans}/> },
                { key: 'plans-inbox', label: 'Plans Inbox', render: () => <PlanInboxTable managePlans={managePlans}/> },
            ]
            : [{ key: 'plans-inbox', label: 'Plans Inbox', render: () => <PlanInboxTable managePlans={managePlans}/> }]
        ),
        { key: 'licensing', label: 'Licensing', render: () => <CompanyLicenseDataGrid/> },
        { key: 'rulers', label: 'Rulers', render: () => <CompanyRulerDataGrid/> },
        { key: 'generate', label: 'Generate License/ Ruler', render: () => (
            <div className="createStuffParentDiv" style={{flexDirection:"row", display:'flex'}}>
                <div className="createStuffSubDiv" style={{width:"50%", border:'solid 2px darkgray', margin:2, borderRadius:5, padding:5}}>
                    <CompanyLicensingCreation selectedCompProp={selectedComp} />
                </div>
                <div className="createStuffSubDiv" style={{width:"50%", border:'solid 2px darkgray', margin:2, borderRadius:5, padding:5}}>
                    <RulerCreator selectedCompProp={selectedComp} />
                </div>
            </div>
        )},
    ];

    // Plans tab appears/disappears with processExternal. Only reset if the selected index is now invalid.
    React.useEffect(() => {
        if (value >= companyModules.length) {
            setValue(0);
        }
    }, [companyModules.length, value]);

    return (
        <Page title="Companies">
            <Box p={3} sx={{ '& .header': { backgroundColor: '#e2e2e2' } }}>
                <Box className="titleParent" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant='h4' textAlign='left' p={2} className="companyTitle">
                        Company Management
                    </Typography>
                </Box>
                <div className="searchBars" style={{justifySelf:'left', display:'flex', flexDirection:'row', width:'100%'}}>
                    <div className="searchSection" style={{width:'33%', margin:"2px"}}>
                        <FormControl style={{width:'100%', margin:"2px"}}>
                            <Autocomplete
                                disablePortal
                                {...defaultProps}
                                renderInput={(params) => <TextField value={params.id} {...params} label="Companies" />}
                                onChange={(event, newValue) => {
                                    const companyId = newValue?.id;
                                    setSelectedComp(companyId ?? EMPTY_GUID);
                                    const companyInfoIndex = companyModules.findIndex(
                                        (module) => module.key === 'company-info',
                                    );
                                    if (
                                        companyId
                                        && companyModules[value]?.key === 'privileges'
                                        && companyInfoIndex >= 0
                                    ) {
                                        setValue(companyInfoIndex);
                                    }
                                }}
                            />
                        </FormControl>
                    </div>
                    <div className="searchSection" style={{width:"33%", margin:'2px'}}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box>
                                <DatePicker
                                    className='DatePicker'
                                    views={['year']}
                                    label="Year"
                                    value={endDate}
                                    onChange={ (selectedValue) => {
                                        setStartDate(dayjs(selectedValue).startOf('year'));
                                        setExportStartDate(dayjs(selectedValue).startOf('year').format());
                                        setEndDate(dayjs(selectedValue).endOf('year'));
                                        setExportEndDate(dayjs(selectedValue).endOf('year').format());
                                    }}
                                    renderInput={ (params) => <TextField {...params} />}
                                />
                            </Box>
                        </LocalizationProvider>
                        <Button variant='contained' style={{ width:"50%", margin:"2px"}} onClick={()=>{filterCompany()}}>Filter Company List</Button>
                    </div>
                    <div className="searchSection">
                        <Button variant='contained' onClick={()=>{resetCompany()}}>Reset Company List</Button>
                    </div>
                </div>
                <Tabs value={value} onChange={handleTabChange} style={{ borderBottom: '2px solid darkGray', paddingBottom: '10px',overflowX:"scroll", scrollbarWidth:'none'}}>
                    {companyModules.map((module) => (
                        <Tab key={module.key} label={module.label} />
                    ))}
                </Tabs>
                <AdminCompanyProvider companyId={companyData?.id} companyData={companyData}>
                    {companyModules.map((module, index) => (
                        <TabPanel unmountInactive key={module.key} className="companyTab" value={value} index={index} style={{textAlign:'left'}}>
                            {module.render()}
                        </TabPanel>
                    ))}
                </AdminCompanyProvider>
            </Box>
        </Page>
    )
};

export default CompanyViewer;
